import { useEffect, useRef, useState } from 'react';
import {
  calcularRms, limiarDaSensibilidade, passoDoGate, aproximar, nivelParaBarra,
  potenciaPorBin, razaoBandaDeVoz, planicidadeEspectral, pareceVoz,
} from '../helpers/audioGate';

// ---------------------------------------------------------------------------
// Volume e "ouvir so quando falar" por participante.
//
// DECISAO IMPORTANTE: o audio continua tocando pelo elemento <video>. O
// WebAudio entra SO PARA ANALISAR (o AnalyserNode nunca e conectado ao
// destination). Roteando a reproducao pelo WebAudio a gente ganharia ganho
// acima de 100%, mas perderia o `setSinkId` do elemento — que e como a
// escolha de saida de audio (fone x alto-falante) funciona hoje. Nao vale a
// troca: o limite pratico e atenuar (0-100%), que ja resolve equilibrar quem
// esta alto demais com quem esta baixo.
//
// Consequencia a saber: `video.volume` nao passa de 1.0. Se um dia for
// preciso AUMENTAR alem do normal, tem que mover a reproducao para o
// WebAudio e trocar `video.setSinkId` por `AudioContext.setSinkId`.
// ---------------------------------------------------------------------------

// Periodo do laco de analise. ~40 leituras/s: mais que suficiente para um
// gate (o hold e de 400ms) e barato — um RMS sobre 512 amostras nao custa
// nada. Com PASSO_FADE de 0.2, o fade leva 5 tiques (~125ms).
const INTERVALO_MS = 25;

// Um AudioContext por aba, criado sob demanda. Cada tile criar o seu
// desperdicaria recurso e o Chrome limita a quantidade por pagina.
let contextoCompartilhado = null;
function obterContexto() {
  if (!contextoCompartilhado) {
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return null;
    contextoCompartilhado = new Ctor();
  }
  // Autoplay policy: o contexto nasce suspenso se nao houver gesto. Entrar na
  // sala e um clique, entao aqui ja da para retomar.
  if (contextoCompartilhado.state === 'suspended') {
    contextoCompartilhado.resume().catch(() => {});
  }
  return contextoCompartilhado;
}

export default function useAudioParticipante({
  elementoRef, stream, volume = 1, gate = false, sensibilidade = 50, medindo = false,
}) {
  const [nivel, setNivel] = useState(0);
  const [falando, setFalando] = useState(false);
  // Ha som chegando, mas o criterio espectral entendeu como ruido. Serve para
  // a UI explicar por que esta mudo em vez de so parecer quebrado.
  const [ruidoIgnorado, setRuidoIgnorado] = useState(false);

  // Os valores entram por ref para o laco de animacao nao precisar reiniciar
  // a cada mexida no slider.
  const cfg = useRef({ volume, gate, sensibilidade });
  cfg.current = { volume, gate, sensibilidade };

  // Sem gate, o volume e aplicado direto — nao precisa de laco nenhum.
  useEffect(() => {
    const el = elementoRef.current;
    if (!el || gate) return;
    el.volume = volume;
  }, [elementoRef, volume, gate]);

  useEffect(() => {
    const el = elementoRef.current;
    const precisaAnalisar = gate || medindo;
    if (!el || !precisaAnalisar || !stream?.getAudioTracks?.().length) return undefined;

    const ctx = obterContexto();
    if (!ctx) return undefined;

    let fonte;
    try {
      fonte = ctx.createMediaStreamSource(stream);
    } catch (_) {
      return undefined; // stream sem audio utilizavel
    }

    const analisador = ctx.createAnalyser();
    // 2048 (nao 512): com 512 cada bin cobre ~94 Hz e os harmonicos da voz,
    // espacados por ~150 Hz, ficariam indistinguiveis — a planicidade
    // espectral nao teria o que medir. Com 2048 sao ~23 Hz por bin.
    analisador.fftSize = 2048;
    analisador.smoothingTimeConstant = 0.2;
    // NAO conecta em ctx.destination: se conectasse, o som tocaria duas vezes
    // (aqui e no <video>).
    fonte.connect(analisador);

    const amostras = new Float32Array(analisador.fftSize);
    const espectro = new Float32Array(analisador.frequencyBinCount);
    const taxa = ctx.sampleRate;
    let estado = { aberto: false, fechaEm: 0 };
    let volumeAtual = el.volume;
    let ultimoTique = performance.now();

    // Abre o gate e devolve o volume cheio. Usado quando a aba nao esta
    // visivel (ver abaixo) e na limpeza.
    const abrirTudo = () => {
      estado = { aberto: true, fechaEm: 0 };
      volumeAtual = cfg.current.volume;
      el.volume = Math.min(Math.max(volumeAtual, 0), 1);
      setFalando(false);
      setNivel(0);
      setRuidoIgnorado(false);
    };

    const passo = () => {
      // ABA ESCONDIDA: abre o gate e nao decide nada.
      //
      // O navegador estrangula timers (e para o requestAnimationFrame por
      // completo) quando a pagina nao esta renderizando. Insistir no gate aqui
      // arriscaria deixar alguem MUDO enquanto voce trabalha em outra aba —
      // que e justamente quando voce depende do audio. Entre errar para mais
      // ou para menos, ouvir demais e o erro seguro.
      if (document.hidden) {
        if (!estado.aberto || volumeAtual !== cfg.current.volume) abrirTudo();
        ultimoTique = performance.now();
        return;
      }

      const agora = performance.now();
      const decorrido = agora - ultimoTique;
      ultimoTique = agora;

      analisador.getFloatTimeDomainData(amostras);
      const rms = calcularRms(amostras);
      const { volume: vol, gate: comGate, sensibilidade: sens } = cfg.current;

      let ehRuido = false;
      if (comGate) {
        const limiar = limiarDaSensibilidade(sens);
        let abrir = false;

        if (rms >= limiar) {
          // So calcula o espectro quando o nivel ja passou: a FFT e o trecho
          // mais caro do laco e nao adianta nada no silencio.
          analisador.getFloatFrequencyData(espectro);
          const potencias = potenciaPorBin(espectro);
          abrir = pareceVoz({
            rms,
            limiar,
            razaoVoz: razaoBandaDeVoz(potencias, taxa),
            planicidade: planicidadeEspectral(potencias, taxa),
          });
          ehRuido = !abrir;
        }

        estado = passoDoGate({
          abrir,
          aberto: estado.aberto,
          fechaEm: estado.fechaEm,
          agora,
        });
      } else {
        estado = { aberto: true, fechaEm: 0 };
      }

      // Fade em vez de corte seco: evita estalo ao abrir/fechar.
      const alvo = estado.aberto ? vol : 0;
      if (volumeAtual !== alvo) {
        volumeAtual = aproximar(volumeAtual, alvo, decorrido);
        el.volume = Math.min(Math.max(volumeAtual, 0), 1);
      }

      setFalando(comGate && estado.aberto);
      setRuidoIgnorado(ehRuido && !estado.aberto);
      setNivel(nivelParaBarra(rms));
    };

    // setInterval, NAO requestAnimationFrame: o rAF e pausado sempre que a
    // pagina nao esta renderizando (aba em segundo plano, janela minimizada,
    // outra janela por cima). Com rAF o gate congelava no ultimo estado.
    const timer = setInterval(passo, INTERVALO_MS);
    // Reage na hora ao voltar/sair da aba, sem esperar o proximo tique.
    document.addEventListener('visibilitychange', passo);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', passo);
      try { fonte.disconnect(); } catch (_) { /* ja desconectado */ }
      try { analisador.disconnect(); } catch (_) { /* ja desconectado */ }
      // Devolve o volume cheio ao desligar o gate, senao o participante
      // poderia ficar mudo para sempre se o laco parar com o gate fechado.
      if (el) el.volume = cfg.current.volume;
      setFalando(false);
      setNivel(0);
    };
  }, [elementoRef, stream, gate, medindo]);

  return { nivel, falando, ruidoIgnorado };
}
